import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import apiClient from "../../store/services/apiClient";
import {
  getPlayerBalance,
  getPlayerCash,
  getUsersByID,
} from "../../store/slices/userActions";
import styles from "./RefundTab.module.css";

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getFrequencyLabel = (value) => {
  switch ((value || "").toUpperCase()) {
    case "DAILY":
      return "รายวัน";
    case "WEEKLY":
      return "รายสัปดาห์";
    case "MONTHLY":
      return "รายเดือน";
    default:
      return value || "-";
  }
};

export default function RefundTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { currentUser, singleUsers } = useSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [cashbackSetting, setCashbackSetting] = useState(null);
  const [pendingCashbacks, setPendingCashbacks] = useState([]);
  const [recentCashbacks, setRecentCashbacks] = useState([]);
  const [feedback, setFeedback] = useState(null);

  const userDetail = useMemo(() => {
    if (Array.isArray(singleUsers) && singleUsers.length) return singleUsers[0];
    if (Array.isArray(singleUsers?.data) && singleUsers.data.length) {
      return singleUsers.data[0];
    }
    return currentUser || user || null;
  }, [currentUser, singleUsers, user]);

  const loadRefundData = useCallback(
    async ({ preserveFeedback = false } = {}) => {
      if (!user?.id) return;

      setIsLoading(true);
      try {
        const [settingRes, pendingRes, recentRes] = await Promise.all([
          apiClient.get("/setting/cashback"),
          apiClient.get(`/agentbalance/cashbackclientcheck/${user.id}`),
          apiClient.get(`/agentbalance/cashbacksummaryclient/${user.id}`),
        ]);

        setCashbackSetting(settingRes?.data?.data?.[0] || null);
        setPendingCashbacks(pendingRes?.data?.data || []);
        setRecentCashbacks(recentRes?.data?.data || []);
        if (!preserveFeedback) {
          setFeedback(null);
        }
      } catch (error) {
        console.error("load refund data error", error?.message || error);
        setFeedback({
          type: "warning",
          title: "โหลดข้อมูลคืนยอดเสียไม่สำเร็จ",
          text: "กรุณาลองใหม่อีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์",
          icon: "solar:danger-triangle-bold",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [user?.id],
  );

  useEffect(() => {
    loadRefundData();
  }, [loadRefundData]);

  const refundData = useMemo(() => {
    const availableAmount = (pendingCashbacks || []).reduce(
      (sum, item) => sum + Number(item?.cashBackAmount || 0),
      0,
    );
    const receivedItems = (recentCashbacks || []).filter(
      (item) => item?.cashBackStatus === "RECEIVED",
    );
    const totalRefunded = receivedItems.reduce(
      (sum, item) => sum + Number(item?.cashBackAmount || 0),
      0,
    );
    const lastRefund = [...receivedItems].sort((left, right) => {
      const leftDate = new Date(
        left?.updated_at || left?.created_at || left?.cashBackDate || 0,
      ).getTime();
      const rightDate = new Date(
        right?.updated_at || right?.created_at || right?.cashBackDate || 0,
      ).getTime();
      return rightDate - leftDate;
    })[0];
    const hasWithdrawCondition =
      !!userDetail?.withdrawConditionType ||
      userDetail?.PromotionStatus === "ACTIVE";

    return {
      availableAmount,
      lastRefundDate: formatDate(
        lastRefund?.updated_at ||
          lastRefund?.created_at ||
          lastRefund?.cashBackDate,
      ),
      totalRefunded,
      eligibleForRefund: availableAmount > 0 && !hasWithdrawCondition,
      pendingCount: pendingCashbacks.length,
      refundPercentage: Number(
        cashbackSetting?.paidpercent ||
          pendingCashbacks?.[0]?.cashBackPercent ||
          0,
      ),
      frequencyLabel: getFrequencyLabel(cashbackSetting?.paidfrequency),
      claimTime: cashbackSetting?.paiddatetime || "-",
      cashBackDetail: cashbackSetting?.cashBackDetail || "",
      withdrawConditionType: cashbackSetting?.withdrawConditionType || "NONE",
      withdrawConditionDetail: cashbackSetting?.withdrawConditionDetail || "",
      limitWithdraw: Number(cashbackSetting?.limitWithdraw || 0),
      hasWithdrawCondition,
    };
  }, [cashbackSetting, pendingCashbacks, recentCashbacks, userDetail]);

  const amountNumber = Number(refundData.availableAmount || 0);
  const claimStatus = useMemo(() => {
    if (feedback) {
      return feedback;
    }

    if (isLoading) {
      return {
        type: "warning",
        title: "กำลังโหลดข้อมูลคืนยอดเสีย",
        text: "ระบบกำลังตรวจสอบรายการล่าสุดของคุณ",
        icon: "solar:refresh-circle-bold",
      };
    }

    if (refundData.eligibleForRefund) {
      return {
        type: "success",
        title: "พร้อมรับเงินคืน",
        text: `มียอดคืนที่รอรับ ${refundData.pendingCount} รายการ รวม ${formatMoney(refundData.availableAmount)} บาท`,
        icon: "solar:check-circle-bold",
      };
    }

    if (refundData.hasWithdrawCondition && amountNumber > 0) {
      return {
        type: "warning",
        title: "มียอดคืน แต่ยังติดเงื่อนไขถอนหรือโปรโมชั่น",
        text: "ต้องเคลียร์เงื่อนไขปัจจุบันก่อน จึงจะกดรับคืนยอดเสียได้",
        icon: "solar:danger-triangle-bold",
      };
    }

    return {
      type: "warning",
      title: "ยังไม่มียอดที่รับคืนได้",
      text: "โปรดติดต่อแอดมินหากเกิดข้อผิดพลาด",
      icon: "solar:danger-triangle-bold",
    };
  }, [amountNumber, feedback, isLoading, refundData]);

  const handleClaimRefund = async () => {
    if (!refundData.eligibleForRefund || isClaiming || !user?.id) return;

    setIsClaiming(true);
    try {
      const res = await apiClient.post(`/agentbalance/paidcashback/${user.id}`);
      if (res?.data?.success) {
        setFeedback({
          type: "success",
          title: "รับคืนยอดเสียสำเร็จ",
          text:
            res?.data?.message || "ระบบเพิ่มเครดิตคืนยอดเสียให้เรียบร้อยแล้ว",
          icon: "solar:check-circle-bold",
        });
        await loadRefundData({ preserveFeedback: true });
        dispatch(getUsersByID(user.id));
        dispatch(getPlayerBalance(user.id));
        dispatch(getPlayerCash(user.id));
      } else {
        setFeedback({
          type: "warning",
          title: "ยังไม่สามารถรับคืนยอดเสียได้",
          text: res?.data?.message || "ระบบไม่อนุญาตให้ทำรายการในตอนนี้",
          icon: "solar:danger-triangle-bold",
        });
      }
    } catch (error) {
      console.error("claim refund error", error?.message || error);
      setFeedback({
        type: "warning",
        title: "รับคืนยอดเสียไม่สำเร็จ",
        text:
          error?.response?.data?.message ||
          "เกิดข้อผิดพลาดระหว่างเชื่อมต่อกับระบบ",

        icon: "solar:danger-triangle-bold",
      });
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.summaryGrid}>
        <div className={styles.balanceCard}>
          <div>
            <div className={styles.balanceLabel}>ยอดรับคืนที่กดรับได้</div>
            <div className={styles.balanceValue}>
              {amountNumber.toLocaleString("th-TH", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              <span className={styles.balanceUnit}>THB</span>
            </div>
          </div>
          <div className={styles.balanceIcon}>
            <Icon icon="solar:hand-money-bold-duotone" width={28} />
          </div>
          <div className={styles.balanceGlow} />
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoMiniCard}>
          <div className={styles.infoMiniLabel}>อัตราคืนยอดเสีย</div>
          <div className={styles.infoMiniValue}>
            {refundData.refundPercentage}%
          </div>
        </div>
        <div className={styles.infoMiniCard}>
          <div className={styles.infoMiniLabel}>รอบการรับคืน</div>
          <div className={styles.infoMiniValue}>
            {refundData.frequencyLabel}
          </div>
        </div>
        <div className={styles.infoMiniCard}>
          <div className={styles.infoMiniLabel}>เวลาเปิดรับ</div>
          <div className={styles.infoMiniValue}>{refundData.claimTime}</div>
        </div>
      </div>

      <div className={styles.formCard}>
        <div className={styles.sectionHeader}>
          <Icon icon="solar:hand-money-bold-duotone" width={20} />
          <span>รายละเอียดการรับคืนยอดเสีย</span>
        </div>

        <button
          type="button"
          className={styles.submitBtn}
          onClick={handleClaimRefund}
          disabled={!refundData.eligibleForRefund || isClaiming || isLoading}
        >
          {isClaiming ? (
            <Icon
              icon="solar:refresh-circle-bold-duotone"
              width={24}
              style={{ animation: "spin 1s linear infinite" }}
            />
          ) : (
            <Icon icon="solar:card-transfer-bold-duotone" width={24} />
          )}
          {isClaiming ? "กำลังรับเงินคืน..." : "ยืนยันรับเงินคืน"}
        </button>

        <div
          className={`${styles.statusMsg} ${claimStatus.type === "success" ? styles.msgSuccess : styles.msgWarning}`}
        >
          <Icon icon={claimStatus.icon} width={20} />
          <div>
            <div className={styles.statusTitle}>{claimStatus.title}</div>
            <div className={styles.statusText}>{claimStatus.text}</div>
          </div>
        </div>
      </div>

      {refundData.lastRefundDate && (
        <div className={styles.historyCard}>
          <div className={styles.sectionHeader}>
            <Icon icon="solar:history-bold-duotone" width={20} />
            <span>ประวัติการรับล่าสุด</span>
          </div>
          <div className={styles.historyText}>{refundData.lastRefundDate}</div>
        </div>
      )}

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
